import { Component, OnInit } from '@angular/core';
import { Router } from '@angular/router';
import { TranslocoService } from '@ngneat/transloco';
import { ConfirmationService, MessageService } from 'primeng/api';
import { HttpErrorResponse } from '@angular/common/http';
import { switchMap, tap } from 'rxjs';
import { VcuService } from 'src/app/service/vcu.service';
import { VcuDevice, VcuApiError } from 'src/app/model/vcu-pair.model';
import { AccountService } from 'src/app/service/account.service';
import { AuthService } from 'src/app/service/auth.service';
import { ModelService } from 'src/app/service/model.service';
import { PlanFeatureService } from 'src/app/service/plan-feature.service';
import { Vcu } from 'src/app/model/form/vcu';
import { Item } from 'src/app/model/Item';

@Component({
  selector: 'app-vcu-list-pair',
  templateUrl: './vcu-list-pair.component.html',
  styleUrls: ['./vcu-list-pair.component.css'],
  providers: [MessageService]
})
export class VcuListPairComponent implements OnInit {
  vcus: VcuDevice[] = [];
  loading: boolean = true;
  displayPairDialog: boolean = false;

  allOfflineVcus: Vcu[] = [];
  loadingOffline: boolean = false;

  get offlineVcus(): Vcu[] {
    const pairedSerials = new Set(this.vcus.map(v => v.serialNumber));
    return this.allOfflineVcus.filter(v => !pairedSerials.has(v.serialNumber));
  }
  showVcuDialog: boolean = false;
  vcuEdit: Vcu;
  editMode: boolean = false;
  modelOptions: any[] = [];
  showVcuLimitModal: boolean = false;
  currentPlanId: string | null = null;
  billingType: 'stripe' | 'manual' | null = null;

  private userID: string | null = null;

  constructor(
    private vcuService: VcuService,
    private accountService: AccountService,
    private authService: AuthService,
    private modelService: ModelService,
    private planFeatureService: PlanFeatureService,
    private messageService: MessageService,
    private confirmationService: ConfirmationService,
    private translocoService: TranslocoService,
    private router: Router
  ) {
    this.vcuEdit = new Vcu();
  }

  ngOnInit(): void {
    this.loadVcus();
    this.userID = this.authService.getUserInformations();
    if (this.userID) {
      this.vcuService.setUserID(this.userID);
      this.accountService.getAccount(this.userID).subscribe({
        next: (account) => {
          this.planFeatureService.load(account.planInformation);
          this.currentPlanId = this.planFeatureService.planId;
          this.billingType = this.planFeatureService.billingType;
        }
      });
      this.loadOfflineVcus();
    }
  }

  loadVcus(): void {
    this.loading = true;
    this.vcuService.getPairedVcus().subscribe({
      next: (response) => {
        this.vcus = (response?.vcus ?? []).filter(vcu => vcu.status === 'AC');
        this.loading = false;
      },
      error: (err: HttpErrorResponse) => {
        this.loading = false;
        this.vcus = [];
        this.handleHttpError(err, 'vcuPair.messages.loadError');
      }
    });
  }

  loadOfflineVcus(): void {
    if (!this.userID) return;
    this.loadingOffline = true;
    this.modelService.getAllModel().pipe(
      tap(dataModel => {
        this.modelOptions = ModelService.toItemList(dataModel);
      }),
      switchMap(() => this.accountService.FindUserVcus(this.userID ?? '', 'AC,AP'))
    ).subscribe({
      next: (data) => {
        if (data) {
          data.forEach((vcudata: any) => {
            if (vcudata.vcuAttributes?.model) {
              const nameModel = this.modelOptions.filter(m => m.id === vcudata.vcuAttributes?.model);
              if (nameModel.length > 0) {
                vcudata.model = new Item(vcudata.vcuAttributes?.model, nameModel[0].name);
              }
            }
          });
          this.allOfflineVcus = VcuService.userVcuToVcus(data);
        }
        this.loadingOffline = false;
      },
      error: () => {
        this.loadingOffline = false;
      }
    });
  }

  createVcu(): void {
    const maxVcus = this.planFeatureService.featureLimit('MaxVcus');
    if (maxVcus > 0 && this.allOfflineVcus.length >= maxVcus) {
      this.messageService.add({
        severity: 'warn',
        summary: this.translocoService.translate('vcu.limitExceeded.title'),
        detail: this.translocoService.translate('vcu.limitExceeded.message', { max: maxVcus }),
        life: 4000
      });
      this.showVcuLimitModal = true;
      return;
    }
    this.editMode = false;
    this.showVcuDialog = true;
  }

  updateOfflineVcuDialog(): void {
    this.showVcuDialog = false;
    this.loadOfflineVcus();
  }

  onPlanUpgraded(): void {
    if (!this.userID) return;
    this.accountService.getAccount(this.userID).subscribe({
      next: (account) => {
        this.planFeatureService.load(account.planInformation);
        this.currentPlanId = this.planFeatureService.planId;
        this.billingType = this.planFeatureService.billingType;
      }
    });
  }

  openPairDialog(): void {
    this.displayPairDialog = true;
  }

  onPairDialogClosed(): void {
    this.loadVcus();
  }

  onEditNickname(vcu: VcuDevice): void {
    if (!vcu.nickname) return;

    this.vcuService.updateVcuNickname(vcu.serialNumber, vcu.nickname).subscribe({
      next: (updatedVcu: VcuDevice) => {
        const idx = this.vcus.findIndex(v => v.serialNumber === updatedVcu.serialNumber);
        if (idx >= 0) {
          this.vcus[idx] = updatedVcu;
        }
        this.messageService.add({
          severity: 'success',
          summary: this.translocoService.translate('common.labels.success'),
          detail: this.translocoService.translate('vcuPair.messages.updateSuccess')
        });
      },
      error: (err: HttpErrorResponse) => {
        this.handleHttpError(err, 'vcuPair.messages.updateError');
      }
    });
  }

  onUnpairVcu(vcu: VcuDevice): void {
    this.confirmationService.confirm({
      key: 'vcuPair',
      message: this.translocoService.translate('vcuPair.messages.confirmUnpair', { name: vcu.nickname || vcu.serialNumber }),
      header: this.translocoService.translate('vcuPair.messages.confirmUnpairHeader'),
      icon: 'pi pi-exclamation-triangle',
      acceptLabel: this.translocoService.translate('vcuPair.messages.yes'),
      rejectLabel: this.translocoService.translate('vcuPair.messages.no'),
      accept: () => {
        this.vcuService.unpairVcu(vcu.serialNumber).subscribe({
          next: (response) => {
            this.messageService.add({
              severity: 'success',
              summary: this.translocoService.translate('common.labels.success'),
              detail: response.message
            });
            this.loadVcus();
          },
          error: (err: HttpErrorResponse) => {
            this.handleHttpError(err, 'vcuPair.messages.unpairError');
          }
        });
      }
    });
  }

  private handleHttpError(err: HttpErrorResponse, fallbackKey: string): void {
    if (err.status === 401) {
      this.router.navigate(['/login']);
      return;
    }

    if (err.status === 403) {
      this.messageService.add({
        severity: 'error',
        summary: this.translocoService.translate('common.labels.error'),
        detail: this.translocoService.translate('vcuPair.errors.forbidden')
      });
      return;
    }

    if (err.status === 500) {
      this.messageService.add({
        severity: 'error',
        summary: this.translocoService.translate('common.labels.error'),
        detail: this.translocoService.translate('vcuPair.errors.internalError')
      });
      return;
    }

    const apiError = err.error as VcuApiError;
    if (apiError?.error) {
      switch (apiError.error) {
        case 'NOT_FOUND':
          this.messageService.add({
            severity: 'error',
            summary: this.translocoService.translate('common.labels.error'),
            detail: this.translocoService.translate('vcuPair.errors.notFound')
          });
          return;
        case 'USER_NOT_FOUND':
          this.messageService.add({
            severity: 'error',
            summary: this.translocoService.translate('common.labels.error'),
            detail: this.translocoService.translate('vcuPair.errors.userNotFound')
          });
          return;
        case 'FORBIDDEN':
          this.messageService.add({
            severity: 'error',
            summary: this.translocoService.translate('common.labels.error'),
            detail: this.translocoService.translate('vcuPair.errors.forbidden')
          });
          return;
        case 'ALREADY_PAIRED':
          this.messageService.add({
            severity: 'error',
            summary: this.translocoService.translate('common.labels.error'),
            detail: this.translocoService.translate('vcuPair.errors.alreadyPaired')
          });
          return;
      }
    }

    if (err.status === 0) {
      this.messageService.add({
        severity: 'error',
        summary: this.translocoService.translate('common.labels.error'),
        detail: this.translocoService.translate('vcuPair.errors.network')
      });
      return;
    }

    console.error('Erro HTTP:', err);
    this.messageService.add({
      severity: 'error',
      summary: this.translocoService.translate('common.labels.error'),
      detail: this.translocoService.translate(fallbackKey)
    });
  }
}
